import React from 'react';
import { useErrorHandler } from '~/hooks/useErrorHandler';
import { useAppProcessing } from '~/pages/ApplicationDetails/context';
import { DocumentCheckOutlineIcon, ArrowRightOutlineIcon, ArrowLeftOutlineIcon } from '@applyboard/ui-icons';
import {
  Dialog,
  Button,
  Flex,
  Text,
  ButtonLink,
  CheckboxGroup,
  Spinner,
  TextInput,
  Textarea,
  // DatePicker,
  useToasts,
} from '@applyboard/crystal-ui';

import { useApplicationWaitlistCondition } from '../../../data/useWaitlistConditions';
import { useIssueWaitlistLetterActivity } from './useIssueWaitlistLetterActivity';
import { OfferClassificationCard } from '../components/OfferClassificationCard';
import { useDeferralRequestActivity } from '../DeferralRequest/useDeferralRequestActivity';
import { DialogHeader, STEPS, OfferClassification } from '../IssueOffer';

export function WaitlistLetterDialog({
  applicationId,
  patchDeferralRequest,
}: {
  applicationId: string;
  patchDeferralRequest?: boolean;
}) {
  const { apiError } = useErrorHandler();
  const { completeProcess, application, startProcess } = useAppProcessing();
  const [loading, setLoading] = React.useState(false);
  const [downloadUrl, setDownloadUrl] = React.useState('');
  const [conditions, setConditions] = React.useState<Array<string>>(
    application.applicationWaitlistConditions.filter((c) => c.code !== 'other').map((c) => c.code)
  );
  const [customCondition, setCustomCondition] = React.useState(
    application.applicationWaitlistConditions.find((c) => c.code === 'other')?.name || ''
  );
  const [additionalNotes, setAdditionalNotes] = React.useState('');
  // TODO: ADDED waitlistConditionDeadline to the waitlist letter dialog, but not used yet
  // const [waitlistConditionDeadline, setWaitlistConditionDeadline] = React.useState<string>('');
  const [stepState, setStepState] = React.useState<number>(STEPS.STEP1);

  // Determine initial offer classification based on persisted conditions
  // Default to Unconditional when no existing conditions (e.g., after reissuing as unconditional)
  const initialOfferClassification = React.useMemo(() => {
    return application.applicationWaitlistConditions.length > 0
      ? OfferClassification.CONDITIONAL
      : OfferClassification.UNCONDITIONAL;
  }, [application.applicationWaitlistConditions]);

  const [offerClassification, setOfferClassification] = React.useState<OfferClassification | ''>(
    initialOfferClassification
  );

  // Handle offer classification change - clear conditions when switching to Unconditional
  const handleOfferClassificationChange = React.useCallback((classification: OfferClassification) => {
    setOfferClassification(classification);
    if (classification === OfferClassification.UNCONDITIONAL) {
      setConditions([]);
      setCustomCondition('');
    }
  }, []);

  // Validation: Offer Type must be selected, and if Conditional, at least one condition must be selected
  const isStep1Valid = React.useMemo(() => {
    if (offerClassification === '') {
      return false;
    }
    if (offerClassification === OfferClassification.UNCONDITIONAL) {
      return true;
    }
    // Conditional: must have at least one predefined condition OR a custom condition
    return conditions.length > 0 || customCondition.trim() !== '';
  }, [offerClassification, conditions, customCondition]);
  const activity = useIssueWaitlistLetterActivity(applicationId);
  const deferralRequestActivity = useDeferralRequestActivity();
  const toasts = useToasts();

  function onOpenChange(open: boolean) {
    if (!open) {
      setStepState(STEPS.STEP1);
      setCustomCondition('');
      setConditions([]);
      setOfferClassification(initialOfferClassification);
      completeProcess();
      setAdditionalNotes('');
      // setWaitlistConditionDeadline('');
    }
  }

  async function onSubmit() {
    setLoading(true);
    if (patchDeferralRequest)
      deferralRequestActivity
        .processDeferralRequest({
          accept: true,
          conditions,
          customCondition,
        })
        .then(() => {
          toasts.positive('The application intake has been updated and a new Waitlist letter has been issued.');
          deferralRequestActivity.completeProcess();
        })
        .catch(() => apiError())
        .finally(() => setLoading(false));
    else
      activity
        .issueWaitlistLetter(conditions, customCondition)
        .then((hasWaitlistLetterActions) => {
          if (hasWaitlistLetterActions) {
            startProcess('additionalDocumentRequest');
          } else {
            completeProcess();
          }
          toasts.positive('Successfully sent waitlist letter. It will be available in details tab shortly.');
        })
        .catch(() => apiError())
        .finally(() => setLoading(false));
  }

  async function onButtonClick1() {
    setLoading(true);
    activity
      .generateWaitlistLetter({
        conditions,
        customCondition,
        deferralRequestId: patchDeferralRequest ? application.meta.deferralRequest!.id : undefined,
        additionalNotes: additionalNotes ? additionalNotes : undefined,
        // waitlistConditionDeadline: waitlistConditionDeadline ? waitlistConditionDeadline : undefined,
      })
      .then(({ letterPreviewDownloadUrl }) => {
        setDownloadUrl(letterPreviewDownloadUrl);
        setStepState(STEPS.STEP2);
      })
      .catch(() => apiError())
      .finally(() => {
        setLoading(false);
      });
  }

  return (
    <Dialog onOpenChange={onOpenChange} size="lg" open={true}>
      <Dialog.Heading>
        <Flex direction="row" gap={2}>
          {patchDeferralRequest && stepState === STEPS.STEP1 && (
            <Button
              aria-label="backButton"
              intent="secondary"
              emphasis="highlighted"
              leadIcon={ArrowLeftOutlineIcon}
              onClick={() => startProcess('deferralRequest')}
            />
          )}
          <DialogHeader
            setStepState={setStepState}
            stepState={stepState}
            heading="Issue Waitlist Letter"
            totalSteps={2}
          />
        </Flex>
      </Dialog.Heading>
      <Dialog.Content>
        <Flex direction="column" gap={8}>
          {stepState === STEPS.STEP1 && (
            <WaitlistLetterConditionGroup
              conditions={conditions}
              setConditions={setConditions}
              customCondition={customCondition}
              setCustomCondition={setCustomCondition}
              setAdditionalNotes={setAdditionalNotes}
              additionalNotes={additionalNotes}
              offerClassification={offerClassification}
              setOfferClassification={handleOfferClassificationChange}
              // setWaitlistConditionDeadline={setWaitlistConditionDeadline}
              // waitlistConditionDeadline={waitlistConditionDeadline}
            />
          )}
          {stepState === STEPS.STEP2 && (
            <>
              <ButtonLink
                aria-label="Download Waitlist Letter LOA"
                emphasis="highlighted"
                href={downloadUrl}
                intent="primary"
                size="lg"
                leadIcon={DocumentCheckOutlineIcon}
                width="fill"
                mode="override_user_choice_and_open_in_new_tab"
              >
                Waitlist letter
              </ButtonLink>
              <Text variant="bodyM" intent="secondary">
                A preview of the waitlist letter is available before sending it to the applicant. Once the letter is
                sent, you will not be able to make changes.
              </Text>
            </>
          )}
        </Flex>
      </Dialog.Content>
      <Dialog.Actions>
        <Dialog.CloseButton disabled={loading} width="fill">
          Cancel
        </Dialog.CloseButton>
        {stepState === STEPS.STEP1 && (
          <Button
            intent="primary"
            loading={loading}
            disabled={!isStep1Valid}
            width="fill"
            trailIcon={ArrowRightOutlineIcon}
            onClick={onButtonClick1}
          >
            Next
          </Button>
        )}
        {stepState === STEPS.STEP2 && (
          <Button intent="primary" loading={loading} onClick={onSubmit} width="fill">
            Send Waitlist letter
          </Button>
        )}
      </Dialog.Actions>
    </Dialog>
  );
}

function WaitlistLetterConditionGroup(props: {
  setConditions: (conditions: Array<string>) => void;
  conditions: Array<string>;
  setCustomCondition: (condition: string) => void;
  customCondition: string;
  setAdditionalNotes: (notes: string) => void;
  additionalNotes: string;
  offerClassification: OfferClassification | '';
  setOfferClassification: (classification: OfferClassification) => void;
  // setWaitlistConditionDeadline?: (date: string) => void;
  // waitlistConditionDeadline?: string;
}) {
  const { waitlistLetterConditions, isFetchingWaitlistLetterCondition } = useApplicationWaitlistCondition();
  // const minDate = new Date();
  // minDate.setDate(minDate.getDate() - 1);

  // const maxDate = new Date();
  // maxDate.setFullYear(maxDate.getFullYear() + 15);

  const isConditional = props.offerClassification === OfferClassification.CONDITIONAL;

  return (
    <>
      <Flex direction="column" gap={2}>
        <Text variant="bodyS" contrast="mid">
          Offer Type{' '}
          <Text as="span" aria-hidden intent="negative" variant="labelM" contrast="mid">
            *
          </Text>
        </Text>
        <Flex direction="row" gap={4}>
          <OfferClassificationCard
            selected={props.offerClassification === OfferClassification.UNCONDITIONAL}
            title="Unconditional"
            subtitle="No additional requirements"
            onClick={() => props.setOfferClassification(OfferClassification.UNCONDITIONAL)}
            aria-label="Unconditional offer type"
          />
          <OfferClassificationCard
            selected={props.offerClassification === OfferClassification.CONDITIONAL}
            title="Conditional"
            subtitle="Student must meet requirements"
            onClick={() => props.setOfferClassification(OfferClassification.CONDITIONAL)}
            aria-label="Conditional offer type"
          />
        </Flex>
      </Flex>
      {isConditional &&
        (isFetchingWaitlistLetterCondition ? (
          <Flex direction="column" align="center">
            <Spinner />
          </Flex>
        ) : (
          <>
            <CheckboxGroup
              onChange={(conditions: Array<string>) => {
                props.setConditions(conditions);
              }}
              value={props.conditions}
              label="Select Applicable Conditions"
            >
              <Flex direction="column">
                {waitlistLetterConditions?.map((i) => (
                  <CheckboxGroup.Option label={i.name} value={i.code} key={i.id} />
                ))}
              </Flex>
            </CheckboxGroup>
            <TextInput
              label="Custom condition"
              intent="secondary"
              onChange={(condition: string) => props.setCustomCondition(condition)}
              value={props.customCondition}
              helpText="Add any conditions that are not included in the list above"
            />
            {/* <DatePicker
              granularity="day"
              label="Condition Deadline"
              maxDate={maxDate.toISOString()}
              minDate={minDate.toISOString()}
              onChange={(date: string) => props.setWaitlistConditionDeadline?.(date)}
              size="md"
              value={props.waitlistConditionDeadline ? props.waitlistConditionDeadline : ''} // controlled input
            /> */}
          </>
        ))}
      <Textarea
        label="Additional Notes"
        intent="secondary"
        onChange={(notes: string) => props.setAdditionalNotes(notes)}
        value={props.additionalNotes}
      />
    </>
  );
}
